import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames';

import JabraInput from 'components/Shared/JabraInput';

import { useFormTheme } from '../../contexts/FormTheme/FormThemeContext';
import { usePrefillableValue } from '../../hooks/usePrefillableValue';
import { EmailWithConfirmationViewModel } from '../../types/emailWithConfirmation';

const TspEmailWithConfirmation: React.FunctionComponent<
  ValueFieldProps<EmailWithConfirmationViewModel>
> = (props) => {
  const { errors, field, ...restProps } = props;

  const [emailValue, setEmailValue] = usePrefillableValue(
    field.model.name,
    props.value,
    props.onChange,
    field.valueField.name
  );
  const [confirmEmailValue, setConfirmEmailValue] = useState(field.model.confirmEmail ?? '');
  const [confirmEmailErrors, setConfirmEmailErrors] = useState<string[]>([]);

  const emailValueRef = useRef(emailValue);
  emailValueRef.current = emailValue;

  const FieldValidationErrors = props.fieldValidationErrorsComponent;
  const Label = props.labelComponent;
  const { primaryTextColor } = useFormTheme();

  const confirmFieldId = `${field.valueField.id}_confirm`;
  const confirmFieldName = field.valueField.name.replace('.Value', '.ConfirmEmail');
  const confirmLabel = field.model.confirmEmailLabel || 'Confirm Email';
  const confirmPlaceholder = field.model.confirmEmailPlaceholder || field.model.placeholderText;

  const validateEmails = useCallback(
    (email: string, confirmEmail: string): string[] => {
      if (!confirmEmail) return [];
      if (email !== confirmEmail) {
        return [field.model.validationErrorMessage || 'Email addresses do not match'];
      }
      return [];
    },
    [field.model.validationErrorMessage]
  );

  useEffect(() => {
    if (emailValue && confirmEmailValue) {
      const validationErrors = validateEmails(emailValue, confirmEmailValue);
      const isValid = validationErrors.length === 0;

      setConfirmEmailErrors(validationErrors);

      props.onChange(field.valueField.name, emailValue, isValid, []);
      props.onChange(confirmFieldName, confirmEmailValue, isValid, []);
    }
  }, [
    emailValue,
    confirmEmailValue,
    props,
    field.valueField.name,
    confirmFieldName,
    field.model.validationErrorMessage,
    validateEmails,
  ]);

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event?.target?.value;
    setEmailValue(newValue);
    emailValueRef.current = newValue;

    const validationErrors = validateEmails(newValue, confirmEmailValue);
    setConfirmEmailErrors(validationErrors);

    const isValid = validationErrors.length === 0;
    props.onChange(field.valueField.name, newValue, isValid, []);
  };

  const onConfirmEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event?.target?.value;
    setConfirmEmailValue(newValue);

    const validationErrors = validateEmails(emailValueRef.current, newValue);
    setConfirmEmailErrors(validationErrors);

    const isValid = validationErrors.length === 0;

    props.onChange(confirmFieldName, newValue, isValid, []);
    props.onChange(field.valueField.name, emailValueRef.current, isValid, []);
  };

  const wrapperClasses = classNames('mb-6 px-3 basis-full md:basis-1/2', field.model.cssClass);

  return (
    <>
      {/* Email Field */}
      <div className={wrapperClasses}>
        {!!Label && <Label {...props} />}
        <JabraInput
          type="email"
          id={field.valueField.id}
          name={field.valueField.name}
          value={emailValue}
          jssMetadata={props}
          placeholder={field.model.placeholderText}
          required={field.model.required}
          onChange={onEmailChange}
        />
        {!!FieldValidationErrors && errors.length > 0 && (
          <FieldValidationErrors
            field={field}
            errors={errors.map((error) =>
              !!field.model.validationErrorMessage ? field.model.validationErrorMessage : error
            )}
            {...restProps}
          />
        )}
      </div>

      {/* Confirm Email Field */}
      <div className={wrapperClasses}>
        <div className={classNames('mb-2 text-sm font-medium', primaryTextColor)}>
          <label htmlFor={confirmFieldId}>{confirmLabel}</label>
          {field.model.required && <span className="pl-0.5 font-medium text-error">*</span>}
        </div>
        <JabraInput
          type="email"
          id={confirmFieldId}
          name={confirmFieldName}
          value={confirmEmailValue}
          jssMetadata={{ ...props, errors: confirmEmailErrors }}
          placeholder={confirmPlaceholder}
          required={field.model.required}
          onChange={onConfirmEmailChange}
        />
        {!!FieldValidationErrors && confirmEmailErrors.length > 0 && (
          <FieldValidationErrors field={field} errors={confirmEmailErrors} {...restProps} />
        )}
      </div>
    </>
  );
};

export default TspEmailWithConfirmation;
